import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MensajeCargando from '../UI/MensajeCargando.js';
import MensajeError from '../UI/MensajeError.js';
import useGetListaURL from '../../hooks/useGetListaURL.js';
import styles from './CuadroDivisionArgentina.module.css';

const ID_ARGENTINA = 1;
const NIVEL_CARGA_AUTOMATICA = 3;

/**
 * Muestra los participantes del torneo más reciente de una división argentina.
 * - nivelActual <= 3: se carga y expande automáticamente al montar.
 * - nivelActual > 3: arranca cerrado; carga los datos solo al expandir por primera vez.
 * Props:
 *   division  – objeto { id, descripcion, nivelActual }
 */
const CuadroDivisionArgentina = ({ division }) => {
  const cargaAutomatica = (division.nivelActual ?? 0) <= NIVEL_CARGA_AUTOMATICA;

  const [expandido, setExpandido] = useState(cargaAutomatica);
  const [cargado, setCargado] = useState(false);
  const [participantes, setParticipantes] = useState(null);
  const [tituloTorneo, setTituloTorneo] = useState('');

  const {
    isCargando: cargandoTorneos,
    erroresGetLista: errTorneos,
    fetchData: fetchTorneos,
  } = useGetListaURL();

  const {
    isCargando: cargandoPart,
    erroresGetLista: errPart,
    fetchData: fetchPart,
  } = useGetListaURL();

  const cargarDatos = useCallback(() => {
    if (cargado || !division || !division.id) return;
    setCargado(true);
    fetchTorneos('paises/' + ID_ARGENTINA + '/torneos?division=' + division.id, (torneos) => {
      if (!torneos || torneos.length === 0) {
        setParticipantes([]);
        setTituloTorneo('');
        return;
      }
      const masReciente = torneos.reduce((prev, curr) => {
        if (curr.anioInicio > prev.anioInicio) return curr;
        if (curr.anioInicio === prev.anioInicio && curr.id > prev.id) return curr;
        return prev;
      });
      setTituloTorneo(masReciente.nombre || String(masReciente.anioInicio));
      fetchPart('torneos/' + masReciente.id + '/participantes', (equipos) => {
        const ordenados = [...(equipos || [])].sort((a, b) =>
          (a.nombre || '').localeCompare(b.nombre || '', 'es')
        );
        setParticipantes(ordenados);
      });
    });
  }, [cargado, division, fetchTorneos, fetchPart]);

  // Carga automática para niveles 1-3
  useEffect(() => {
    if (cargaAutomatica) {
      cargarDatos();
    }
  }, [cargaAutomatica, cargarDatos]);

  const toggleExpandido = () => {
    if (!expandido && !cargado) {
      cargarDatos();
    }
    setExpandido((v) => !v);
  };

  const isCargando = cargandoTorneos || cargandoPart;
  const error = errTorneos || errPart;

  return (
    <div className={styles.cuadro}>
      <div className={styles.encabezado}>
        <span className={styles.titulo}>{division.descripcion}</span>
        <button
          className={styles.botonToggle}
          onClick={toggleExpandido}
          title={expandido ? 'Cerrar' : 'Abrir'}
          aria-expanded={expandido}
        >
          {expandido ? '▲' : '▼'}
        </button>
      </div>
      <div className={expandido ? undefined : styles.contenidoOculto}>
        <MensajeCargando isCargando={isCargando} />
        <MensajeError errores={error} />
        {tituloTorneo && <div className={styles.subtituloTorneo}>{tituloTorneo}</div>}
        {participantes && participantes.length === 0 && !isCargando && (
          <div className={styles.sinDatos}>Sin participantes</div>
        )}
        {participantes && participantes.length > 0 && (
          <ul className={styles.listaEquipos}>
            {participantes.map((equipo) => {
              const primerClub =
                equipo.clubes && equipo.clubes.length > 0 ? equipo.clubes[0] : null;
              const to = primerClub ? '/club/' + primerClub.id : '/equipo/' + equipo.id;
              return (
                <li key={equipo.id} className={styles.itemEquipo}>
                  <Link to={to} className={styles.linkEquipo} target="_blank" rel="noreferrer">
                    {equipo.nombre}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CuadroDivisionArgentina;
